import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Button from '../ui/Button';
import { NAV_LINKS } from '../../data/homeContent';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import logo from '../../assets/bsplnewlogo-removebg-preview.png';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { direction, scrollY } = useScrollDirection();
  const location = useLocation();
  const scrolled = scrollY > 8;
  const hidden = direction === 'down' && scrollY > 120;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-bspl-border transition-shadow duration-300 ${
          scrolled ? 'shadow-navbar' : ''
        }`}
        initial={{ y: 0 }}
        animate={{ y: hidden ? -100 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <nav className="section-container flex h-16 md:h-[72px] items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0 min-w-0 z-10 group"
            aria-label="BSPL Digital Solutions — Home"
          >
            <img
              src={logo}
              alt=""
              className="h-12 sm:h-14 md:h-[52px] w-auto max-w-[140px] sm:max-w-[160px] object-contain object-left transition-transform duration-200 group-hover:scale-[1.02]"
            />
            <span className="hidden md:block leading-tight">
              <span className="font-display font-bold text-base text-bspl-text block">
                BSPL Digital Solutions
              </span>
              <span className="text-[11px] text-bspl-text-muted font-body">IT Solutions</span>
            </span>
          </Link>

          <ul className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {NAV_LINKS.map(({ path, label }) => (
              <li key={path}>
                <Link
                  to={path}
                  className={`text-sm font-medium transition-colors hover:text-bspl-accent ${
                    location.pathname === path
                      ? 'text-bspl-accent'
                      : 'text-bspl-text-secondary'
                  }`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="hidden lg:block">
            <Button to="/contact" variant="secondary" size="sm">
              Get a Quote
            </Button>
          </div>

          <button
            type="button"
            className="lg:hidden p-2 text-bspl-text"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} strokeWidth={1.5} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              className="fixed top-0 right-0 z-[70] h-full w-[min(320px,85vw)] bg-white border-l border-bspl-border shadow-card-hover lg:hidden flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <div className="flex items-center justify-between p-4 border-b border-bspl-border gap-3">
                <Link to="/" className="flex items-center gap-2 min-w-0" onClick={() => setMobileOpen(false)}>
                  <img
                    src={logo}
                    alt="BSPL Digital Solutions"
                    className="h-10 w-auto max-w-[100px] object-contain"
                  />
                </Link>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  className="p-2"
                >
                  <X size={24} strokeWidth={1.5} />
                </button>
              </div>
              <ul className="flex flex-col gap-1 p-4 flex-1">
                {NAV_LINKS.map(({ path, label }) => (
                  <li key={path}>
                    <Link
                      to={path}
                      className={`block px-4 py-3 rounded-card text-base font-medium ${
                        location.pathname === path
                          ? 'bg-bspl-surface text-bspl-accent'
                          : 'text-bspl-text hover:bg-bspl-surface'
                      }`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="p-4 border-t border-bspl-border">
                <Button to="/contact" variant="secondary" className="w-full justify-center">
                  Get a Quote
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="h-16 md:h-[72px]" aria-hidden="true" />
    </>
  );
};

export default Navbar;
